import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { withRouter } from '../../common/withRouter';
import { Notification } from "../../web.common";
import { LayoutContext } from "../layout/siteLayout";
import PageTop from "../layout/PageTop";
import withApi from "../higherOrderComponents/withApi";
import { Filters, FilterInput, FilterSelect } from "./Filters";
import { getQueryStringParameters } from "../../common/utils";
import AddEntityButton from "../misc/AddEntityButton";
import { CATEGORY_OPTIONS, LANGUAGE_OPTIONS, categoryLabel, languageLabel } from "./seoOptions";

const SeoTextsInternal = (props) => {
  const { location, history } = props;
  const [loadedData, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const clickable = { cursor: "pointer", color: "#2a6fb0" };

  useEffect(() => {
    if (!loadedData) {
      loadData();
    }
  });

  const loadData = async () => {
    setIsLoading(false);
    const filters = getQueryStringParameters(location.search);
    try {
      const data = await props.api.seoTexts.searchSeoTexts({ filters });
      setData(Array.isArray(data) ? data : []);
      setIsLoading(true);
    } catch (e) {
      console.error('Error loading SEO texts:', e);
      setData([]);
      setIsLoading(true);
    }
  };

  const header = [
    { name: "Id", label: "Id", render: (r) => r.id },
    { name: "AreaSlug", label: "Area Slug", render: (r) => r.areaSlug },
    { name: "Category", label: "Category", render: (r) => r.categoryName || categoryLabel(r.category) },
    { name: "Language", label: "Language", render: (r) => r.languageName || languageLabel(r.language) },
    { name: "MetaTitle", label: "Meta Title", render: (r) => r.metaTitle || '-' },
    {
      name: "ExtraText",
      label: "Body",
      render: (r) => (
        <span style={{ color: r.extraText ? 'green' : 'grey', fontWeight: 'bold' }}>
          {r.extraText ? 'Yes' : 'No'}
        </span>
      ),
    },
  ];

  return (
    <LayoutContext.Consumer>
      {() => (
        <React.Fragment>
          <Notification newMessage={null} />
          <PageTop
            documentTitle="SEO Texts"
            documentDescription="Manage per-area/category SEO meta and body copy"
          />

          <h2 className="main-page-header">
            SEO Texts
            <AddEntityButton to='/seo-text' />
          </h2>

          <Filters
            initFromUrl
            submitUrl
            validateForm={({ areaSlug, category, language }) => ({ areaSlug, category, language })}
            render={(formData, update) => (
              <div className="filters-block">
                <div className="filters-label-block">
                  <label className="filters-label" htmlFor="areaSlug">Area Slug</label>
                  <FilterInput data={formData} update={update} name="areaSlug" placeholder="Find by area slug" />
                </div>
                <div className="filters-label-block">
                  <label className="filters-label" htmlFor="category">Category</label>
                  <FilterSelect className='filter-select' data={formData} update={update} name='category' options={CATEGORY_OPTIONS} />
                </div>
                <div className="filters-label-block">
                  <label className="filters-label" htmlFor="language">Language</label>
                  <FilterSelect className='filter-select' data={formData} update={update} name='language' options={LANGUAGE_OPTIONS} />
                </div>
                <br />
                <br />
                <div className="filters-submit-wrapper">
                  <input type="submit" value="Search" disabled={!isLoading} />
                </div>
                <hr />
              </div>
            )}
          />

          {loadedData == null ? (
            <span>Loading...</span>
          ) : loadedData.length > 0 ? (
            <article className="data-table-wrap">
              <table className="data-lists component-data-table cell-content-align-top">
                <thead>
                  <tr>
                    {header.map((property) => (
                      <td key={property.name}>{property.label}</td>
                    ))}
                  </tr>
                </thead>
                {loadedData.map((row) => (
                  <tbody key={row.id}>
                    <tr
                      className="hover-listview"
                      style={clickable}
                      onClick={() => history.push(`/seo-text/${row.id}`)}
                    >
                      {header.map((property) => (
                        <td key={property.name}>
                          <strong className="table-cell-label">{property.label}</strong>
                          {property.render(row)}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                ))}
              </table>
            </article>
          ) : (
            <div style={{ padding: "20px", textAlign: "center", color: "grey" }}>
              No SEO texts found matching your search criteria.
            </div>
          )}
        </React.Fragment>
      )}
    </LayoutContext.Consumer>
  );
};

SeoTextsInternal.propTypes = {
  api: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  isLoggedIn: PropTypes.bool,
};

const SeoTexts = withRouter(
  withApi(
    connect(({ user }) => ({ isLoggedIn: !!user }), {})(SeoTextsInternal)
  )
);

SeoTexts.requiresLogin = true;

export default SeoTexts;
